import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDict, useLocale } from "@/lib/i18n/context";

type Row = {
  id: string;
  access_token: string;
  status: string;
  job_title: string | null;
  client_company_name: string | null;
  created_at: string;
};

export function CandidateInterviewsPage() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetch("/api/candidate/interviews", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json() as Promise<{ interviews: Row[] }>;
      })
      .then((data) => {
        if (active) setRows(data.interviews);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (error || !rows) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.careers.myInterviewsTitle}</h1>
        <p className="text-muted">{t.careers.myInterviewsSubtitle}</p>
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted">
            {t.careers.myInterviewsEmpty}
          </CardContent>
        </Card>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-4"
            >
              <div>
                <div className="font-medium">
                  {row.job_title || t.common.generalInterview}
                </div>
                <div className="text-sm text-muted">
                  {row.client_company_name || "—"}
                  {" · "}
                  {format(new Date(row.created_at), "PP", {
                    locale: dateLocale,
                  })}
                </div>
                <div className="mt-1">
                  <Badge tone="neutral">{row.status}</Badge>
                </div>
              </div>
              <Link to={`/interview/${row.access_token}`}>
                <Button size="sm">{t.careers.openInterview}</Button>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
